/**
 * service-pricing client. Mirrors:
 *   apps/service-pricing/src/modules/quote/quote.controller.ts
 *
 * The engine returns 422 for business-rule rejections (NO_TARIFF, etc).
 * We surface those failures as a `QuoteFailure` instead of throwing, so the
 * pricing step in the wizard can render the reason inline.
 */
import { env } from '../env';

import { ApiError, request } from './client';
import type { QuoteFailure, QuoteRequest, QuoteResponse, QuoteResult } from './types';

const baseUrl = (): string => env.servicePricingBaseUrl;

export async function getQuote(req: QuoteRequest): Promise<QuoteResult> {
  try {
    return await request<QuoteResponse>(baseUrl(), '/v1/pricing/quote', {
      method: 'POST',
      body: req,
    });
  } catch (err) {
    if (err instanceof ApiError && err.status === 422) {
      const failure = parseQuoteFailure(err.body);
      if (failure !== null) {
        return failure;
      }
    }
    throw err;
  }
}

function parseQuoteFailure(body: unknown): QuoteFailure | null {
  if (typeof body !== 'object' || body === null) {
    return null;
  }
  const record = body as Record<string, unknown>;
  const candidate = (record['reason'] !== undefined ? record : record['message']) as
    | Record<string, unknown>
    | undefined;
  if (!candidate || typeof candidate !== 'object') {
    return null;
  }
  const reason = (candidate as Record<string, unknown>)['reason'];
  if (typeof reason !== 'string') {
    return null;
  }
  const detail = (candidate as Record<string, unknown>)['detail'];
  return {
    ok: false,
    reason: reason as QuoteFailure['reason'],
    detail: typeof detail === 'string' ? detail : undefined,
  };
}
